'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { gql, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { currentTripStateIdVar } from '../search/currentTripState';
import { currentAvailableHotelFiltersVar, currentHotelFilterSelectionsVar } from '../filter/currentHotelFilters';
import { currentHotelSortCriterionVar } from '../sort';
import { callSideEffects } from '../../util';
import { DateFormats, CurrencyFormats, } from '../../contracts/types';
const HOTEL_SEARCH_RESULTS_QUERY = gql `
    query HotelSearchResults(
        $tripStateId: ID!
        $sortCriterion: SortCriterion
        $filters: HotelFiltersInput
        $dateFormats: DateFormats
        $paging: HotelPage
        $departureDateFormat: DateFormats
        $timeFormat: DateFormats
        $currencyFormat: CurrencyFormats
    ) {
        hotelSearchResults(
            tripStateId: $tripStateId
            filters: $filters
            sortCriterion: $sortCriterion
            paging: $paging
        ) {
            hotelCount
            filteredHotelCount
            isNumberOfNightsAdjusted
            hotels {
                hotel {
                    estabId
                    name
                    starRating
                    numberOfPhotos
                    geography {
                        location
                        country
                        province
                        latitude
                        longitude
                    }
                    tripadvisorFeedback {
                        rating
                        numberOfReviews
                        ratingImageUrl
                        tripAdvisorURL
                        reviews {
                            title
                            comment
                            publishedDate
                            ratingImageUrl
                        }
                    }
                    trustYouReviewClusterId
                }
                skywardsMiles {
                    additionalMiles
                    flightMiles
                }
                pricingSummary {
                    conversionUrl
                    boardTypeDescription
                    price
                    priceFormatted(format: $currencyFormat)
                    pricePerNightFormatted(format: $currencyFormat)
                    pricePerPersonFormatted(format: $currencyFormat)
                    wasPrice
                    wasPriceFormatted(format: $currencyFormat)
                    wasPricePerPerson
                    wasPricePerPersonFormatted(format: $currencyFormat)
                    refundable {
                        refundCutOffDate
                        isRefundable
                    }
                    includedExtras
                    callout {
                        text
                        themeColor
                    }
                    hypeMessages(format: $dateFormats) {
                        title
                        description
                        descriptionHeader
                        type
                    }
                    checkInDate
                    numberOfNights
                    departureDate
                    teaserPrice(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                    teaserPricePerPerson(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                }
                flight {
                    flightId
                    flightDetailsId
                    outboundLeg {
                        duration
                        airlines {
                            name
                            code
                            imageName
                        }
                        duration
                        departureAirport {
                            airportCode
                            title
                        }
                        stops
                        arrivalAirport {
                            airportCode
                            title
                        }
                        departureDateTime {
                            date(format: $departureDateFormat)
                            time(format: $timeFormat)
                        }
                        arrivalDateTime {
                            date(format: $departureDateFormat)
                            time(format: $timeFormat)
                        }
                        totalBagsIncluded
                        dayDifference
                        segments {
                            airline {
                                name
                                imageName
                            }
                            departureAirport {
                                airportCode
                                title
                            }
                            arrivalAirport {
                                airportCode
                                title
                            }
                            flightNumber
                            departureDateFormatted
                            arrivalDateFormatted
                            layoverTime
                            dayDifference
                            operatingAirline {
                                name
                                code
                                imageName
                            }
                            cabinClass
                            departureDateTime {
                                date(format: $departureDateFormat)
                                time(format: $timeFormat)
                            }
                            arrivalDateTime {
                                date(format: $departureDateFormat)
                                time(format: $timeFormat)
                            }
                            duration
                        }
                    }
                    inboundLeg {
                        duration
                        airlines {
                            name
                            code
                            imageName
                        }
                        duration
                        departureAirport {
                            airportCode
                            title
                        }
                        stops
                        arrivalAirport {
                            airportCode
                            title
                        }
                        departureDateTime {
                            date(format: $departureDateFormat)
                            time(format: $timeFormat)
                        }
                        arrivalDateTime {
                            date(format: $departureDateFormat)
                            time(format: $timeFormat)
                        }
                        totalBagsIncluded
                        dayDifference
                        segments {
                            airline {
                                name
                                imageName
                            }
                            departureAirport {
                                airportCode
                                title
                            }
                            arrivalAirport {
                                airportCode
                                title
                            }
                            flightNumber
                            departureDateFormatted
                            arrivalDateFormatted
                            layoverTime
                            dayDifference
                            operatingAirline {
                                name
                                code
                                imageName
                            }
                            cabinClass
                            departureDateTime {
                                date(format: $departureDateFormat)
                                time(format: $timeFormat)
                            }
                            arrivalDateTime {
                                date(format: $departureDateFormat)
                                time(format: $timeFormat)
                            }
                            duration
                        }
                    }
                }
            }
            availableFilters {
                ... on CheckboxFilterGroup {
                    id
                    name
                    open
                    type
                    items {
                        id
                        selected
                        label
                        count
                    }
                    pill
                }
                ... on RangeFilterGroup {
                    id
                    name
                    open
                    type
                    max
                    min
                    upper
                    lower
                    formatString
                    step
                }
                ... on TextFilter {
                    id
                    name
                    open
                    type
                    value
                }
            }
            sortCriterion
            hotels {
                numberOfNights
                hotel {
                    customerFeedback {
                        overallRating
                        numberOfReviews
                        overallRatingDescription
                    }
                    tags {
                        text
                        themeColor
                    }
                }
                pricingSummary {
                    discountTypes
                    cityTaxFormatted
                    wasPriceFormatted(format: $currencyFormat)
                    isSponsored
                    impressionUrl
                    clickUrl
                    conversionUrl
                    deposit
                    depositFormatted(format: $currencyFormat)
                    callout {
                        text
                        themeColor
                    }
                    monthlyInstalment {
                        isAvailable
                        initialPayment
                        initialPaymentFormatted(format: $currencyFormat)
                        subsequentPayment
                        subsequentPaymentFormatted(format: $currencyFormat)
                        numberOfInstalments
                    }
                }
            }
            stopSellMessage {
                message
                title
            }
            tripStateId
        }
    }
`;
const HOTELS_PAGING_TYPE = 1;
export function useHotelSearchResults(sideEffects, pageSize = 15, skip = false) {
    // Needed due to broken contract between graphql-client
    const [lastPageIndex, setLastPageIndex] = useState(0);
    const _a = useQuery(HOTEL_SEARCH_RESULTS_QUERY, {
        notifyOnNetworkStatusChange: true,
        ssr: false,
        skip: skip || !currentTripStateIdVar(),
        variables: {
            paging: {
                limit: pageSize,
                offset: 0,
                pagingType: HOTELS_PAGING_TYPE,
            },
            tripStateId: currentTripStateIdVar(),
            filters: currentHotelFilterSelectionsVar(),
            format: DateFormats.Default,
            sortCriterion: currentHotelSortCriterionVar(),
            departureDateFormat: DateFormats.Fulldate,
            timeFormat: DateFormats.Time,
            currencyFormat: CurrencyFormats.Rounded,
        },
        onCompleted: (completedData) => {
            var _a, _b, _c;
            if ((_a = completedData === null || completedData === void 0 ? void 0 : completedData.hotelSearchResults) === null || _a === void 0 ? void 0 : _a.availableFilters) {
                currentAvailableHotelFiltersVar(completedData.hotelSearchResults.availableFilters);
            }
            if ((_b = completedData === null || completedData === void 0 ? void 0 : completedData.hotelSearchResults) === null || _b === void 0 ? void 0 : _b.sortCriterion) {
                currentHotelSortCriterionVar(completedData.hotelSearchResults.sortCriterion);
            }
            // Needed due to broken contract between graphql-client
            const hotelArrLength = (_c = completedData.hotelSearchResults) === null || _c === void 0 ? void 0 : _c.hotels.length;
            if (!hotelArrLength || hotelArrLength <= pageSize) {
                setLastPageIndex(0);
            }
            if (sideEffects) {
                callSideEffects(sideEffects, completedData);
            }
        },
    }), { data, fetchMore, loading } = _a, others = __rest(_a, ["data", "fetchMore", "loading"]);
    const fetchNext = useCallback(() => {
        fetchMore({
            variables: {
                paging: {
                    offset: pageSize + lastPageIndex,
                    limit: pageSize,
                    pagingType: HOTELS_PAGING_TYPE,
                },
                tripStateId: currentTripStateIdVar(),
                filters: currentHotelFilterSelectionsVar(),
                sort: currentHotelSortCriterionVar(),
            },
        });
        setLastPageIndex(lastPageIndex + pageSize);
    }, [fetchMore, lastPageIndex, pageSize]);
    return [Object.assign({ data, fetchMore, loading: typeof window !== 'undefined' && loading }, others), fetchNext];
}
